import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import Input from "@mui/material/Input";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { register } from "authentication/AuthenticationService";

import Page from "utils/Page";
import { asTitle, isEmailValid, isEmpty, isPasswordValid } from "utils/Utils";

const RegisterPage: React.FC = () => {
	const { t } = useTranslation();

	const [email, setEmail] = useState("");
	const [emailValid, setEmailValid] = useState(true);
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [passwordValid, setPasswordValid] = useState(true);
	const [passwordRepeat, setPasswordRepeat] = useState("");
	const [passwordRepeatValid, setPasswordRepeatValid] = useState(true);
	const [loading, setLoading] = useState(false);
	const [registerFailed, setRegisterFailed] = useState(false);
	const [registerSucceeded, setRegisterSucceeded] = useState(false);

	const onEmailChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>): void => {
			event.preventDefault();
			const emailValue = event.target.value;
			setEmail(emailValue);
			setEmailValid(!isEmpty(emailValue));
		},
		[]
	);

	const onPasswordChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>): void => {
			event.preventDefault();
			const passwordValue = event.target.value;
			setPassword(passwordValue);
			setPasswordValid(!isEmpty(passwordValue));
		},
		[]
	);

	const onPasswordRepeatChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>): void => {
			event.preventDefault();
			const passwordRepeatValue = event.target.value;
			setPasswordRepeat(passwordRepeatValue);
			setPasswordRepeatValid(!isEmpty(passwordRepeatValue));
		},
		[]
	);

	const onShowPassword = useCallback((): void => {
		setShowPassword(!showPassword);
	}, [showPassword]);

	function isFormValid(): boolean {
		let valid = true;
		if (!isPasswordValid(password)) {
			setPasswordValid(false);
			valid = false;
		}
		if (passwordRepeat !== password) {
			setPasswordRepeatValid(false);
			valid = false;
		}
		if (!isEmailValid(email)) {
			setEmailValid(false);
			valid = false;
		}
		return valid;
	}

	function submit(
		event: React.MouseEvent<HTMLButtonElement, MouseEvent>
	): void {
		setRegisterFailed(false);
		setRegisterSucceeded(false);
		event.preventDefault();
		if (isFormValid()) {
			if (!loading) {
				setLoading(true);
				register(email, password)
					.then(() => {
						setRegisterSucceeded(true);
						setEmail("");
						setPassword("");
						setPasswordRepeat("");
					})
					.catch(() => setRegisterFailed(true))
					.finally(() => setLoading(false));
			}
		}
	}

	return (
		<Grid container direction="row" justifyContent="center">
			<Grid item xs={12} sm={8} md={6} lg={4}>
				<Paper sx={{ margin: 2, padding: 2, textAlign: "center" }}>
					<Typography variant="h5" gutterBottom>
						{asTitle(t("registerTitle"))}
					</Typography>
					{registerFailed && (
						<Alert
							sx={{ textAlign: "left" }}
							id="error-message"
							severity="error">
							{t("registerError")}
						</Alert>
					)}
					{registerSucceeded && (
						<Alert
							sx={{ textAlign: "left" }}
							id="success-message"
							severity="success">
							{t("registerSuccess")}
						</Alert>
					)}
					<Box
						component="form"
						sx={{
							"& .MuiTextField-root, .MuiButton-root, .MuiFormControl-root":
								{
									marginTop: 1,
									marginBottom: 1
								}
						}}>
						<TextField
							required
							fullWidth
							id="email"
							type="email"
							label={t("email")}
							error={!emailValid}
							value={email}
							onChange={onEmailChange}
							variant="standard"
						/>
						<FormControl required fullWidth variant="standard">
							<InputLabel htmlFor="password">
								{t("password")}
							</InputLabel>
							<Input
								id="password"
								type={showPassword ? "text" : "password"}
								error={!passwordValid}
								value={password}
								onChange={onPasswordChange}
								endAdornment={
									<InputAdornment position="end">
										<IconButton
											aria-label="toggle password visibility"
											onClick={onShowPassword}
											onMouseDown={e =>
												e.preventDefault()
											}
											size="large">
											{showPassword ? (
												<Visibility />
											) : (
												<VisibilityOff />
											)}
										</IconButton>
									</InputAdornment>
								}
							/>
							<FormHelperText>
								{t("passwordRestrictions")}
							</FormHelperText>
						</FormControl>
						<TextField
							id="passwordRepeat"
							type={"password"}
							required
							fullWidth
							error={!passwordRepeatValid}
							value={passwordRepeat}
							label={t("passwordRepeat")}
							helperText={
								passwordRepeatValid
									? ""
									: t("passwordsDoNotMatch")
							}
							variant="standard"
							onChange={onPasswordRepeatChange}
						/>
						<Button
							id="login"
							variant="contained"
							fullWidth
							disabled={loading}
							onClick={submit}>
							{t("registerButton")}
						</Button>
						<Typography variant="body1">
							<Link to="/login">{t("toLogin")}</Link>
						</Typography>
					</Box>
				</Paper>
			</Grid>
		</Grid>
	);
};

export default RegisterPage;
